import PropTypes from 'prop-types'
import React from 'react'
import CarouselItem from '../CarouselItem/CarouselItem'
import { connect } from 'react-redux'
import { compose } from 'redux'
import SeeMoreCarouselItem from '../../components/SeeMoreCarouselItem/SeeMoreCarouselItem'
import focusable from '../../components/Focusable/Focusable'
import { platformTransitionStyle } from '../../../../shared/helpers'
import tts from '../../lib/TTS'

export class Carousel extends React.Component {
  constructor(props) {
    super(props)

    this.focusDebounce = null
    this.offsetAdjustment = 0
    this.carouselItemWidth = null
    this.carouselWidth = null

    this.state = {
      currentIndex: 0,
      direction: '',
      slideOffset: 0,
      endingPoint: 4,
      ready: true,
    }

    this.isSearchScreen =
      this.props.ui.focusableComponents.screen === 'SearchScreen'

    this.hasMoreContent =
      !this.isSearchScreen &&
      (!!props.collection?.meta?.links?.next ||
        props.collection?.content?.length >= 12)

    this.items = []
    this.carouselRef = React.createRef()
    this.carouselItemRef = React.createRef()
    this.selectedCarouselItemRef = React.createRef()
    this.selectedCarouselElementRef = React.createRef()
    this.hasMoreContentButtonRef = React.createRef()
  }

  componentDidMount() {
    // we need to check what kind of content is in the carousel to determine the default number of items to preload
    const endingPoint =
      this.items[0]?.instance?.props?.content?.item_type === 'show' ? 7 : 5

    this.setState({ endingPoint })
    this.setMarginBottom()
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.resetCarousel) {
      if (prevProps.resetCarousel !== this.props.resetCarousel) {
        this.setState({ slideOffset: 0, currentIndex: 0 })
      }
    } else {
      if (
        this.state.currentIndex !== prevState.currentIndex &&
        !prevProps.resetCarousel
      ) {
        this.handleCardFocus()

        if (!this.carouselItemWidth || !this.carouselWidth) {
          this.carouselItemWidth =
            this.selectedCarouselElementRef.current.clientWidth
          this.carouselWidth = this.carouselRef.current.clientWidth
        }

        const lastViewportItemIndex = Math.floor(
          this.carouselWidth / this.carouselItemWidth
        )

        if (this.state.currentIndex >= lastViewportItemIndex) {
          this.setState({
            slideOffset: -(
              this.carouselItemWidth *
              (1 + this.state.currentIndex - lastViewportItemIndex)
            ),
          })
        } else if (this.state.currentIndex >= 0) {
          this.setState({
            slideOffset: 0,
          })
        }
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.focusDebounce)
  }

  onKeyEvent(key) {
    let handled = false
    tts.stopRead()

    if (key === 'LEFT') {
      if (this.state.currentIndex > 0) {
        this.handleLeftKey()
        handled = true
      }
    } else if (key === 'RIGHT') {
      this.handleRightKey()
      handled = true
    }
    return handled
  }

  handleLeftKey = () => {
    this.setState((prevState) => {
      const current =
        prevState.currentIndex > 0 ? prevState.currentIndex - 1 : 0
      return {
        ...prevState,
        currentIndex: current,
      }
    })
  }

  handleRightKey = () => {
    let isLastItem =
      this.state.currentIndex === this.props.collection.content.length - 1
    if (isLastItem && this.hasMoreContent) {
      this.setState((prevState) => {
        return {
          ...prevState,
          currentIndex: prevState.currentIndex + 1,
        }
      })
    } else {
      this.setState((prevState) => {
        const current =
          prevState.currentIndex < this.props.collection.content.length - 1
            ? prevState.currentIndex + 1
            : prevState.currentIndex
        return {
          ...prevState,
          currentIndex: current,
        }
      })
    }
    if (
      this.isSearchScreen &&
      this.props.collection.content.length - this.state.currentIndex === 3
    ) {
      this.props.handleNearEndOfCarousel()
    }
  }

  /**
   * Determines the margin-bottom (in px) depending on the TV model and if we are showing extra details.
   *
   * @param isShowcase boolean for the speacial case of the Showcase
   * @param itemType the type of the first item carousel's children
   */
  getMarginBottom = (isShowcase, itemType) => {
    //If it's a showcase or video, it has 2 lines of metadata
    //If it's a show, it doesn't have any details and is a different size

    if (isShowcase || itemType === 'video') {
      return 132
    } else {
      return 40
    }
  }

  setMarginBottom() {
    let marginBottom
    if (this.props.collection.content[0] !== undefined) {
      marginBottom = this.getMarginBottom(
        this.props.collection.title === 'Spotlight' ||
          this.props.collection.slug === 'search-suggested-shows',
        this.props.collection.content[0].item_type
      )
    } else {
      marginBottom = this.getMarginBottom(
        this.props.collection.title === 'Spotlight',
        'show'
      )
    }

    this.setState({ marginBottom })
  }

  setChildFocus() {
    tts.pushTextToRead(this.carouselTitle)
    this.handleCardFocus()
  }

  handleCardFocus() {
    const isLastItem =
      this.state.currentIndex === this.props.collection.content.length
    if (isLastItem && this.hasMoreContent) {
      this.hasMoreContentButtonRef.current.setFocus()
      this.ttsSeeAllCard()
    } else {
      this.selectedCarouselItemRef.current.setFocus()
    }
  }

  ttsSeeAllCard() {
    tts.addTextToRead(
      `${this.state.currentIndex + 1} of ${
        this.props.collection.content.length + 1
      } ${this.hasMoreContentButtonRef.current.props.content.title}`
    )
  }

  render() {
    const {
      slug,
      hideTitle,
      hideMetaData,
      parentTitle,
      listHasSeasons,
      emptySet,
    } = this.props

    let { collection } = this.props

    const { slideOffset, ready, marginBottom } = this.state

    this.carouselTitle = collection.title

    const showcase = 'Spotlight' === collection.title
    let itemCount = collection.content.length

    if (this.hasMoreContent) {
      itemCount += 1
    }

    return (
      <>
        {ready && (
          <div
            className="carousel-container"
            style={{ marginBottom: marginBottom }}
            ref={this.carouselRef}
          >
            {collection && 0 !== collection.content.length && (
              <>
                <div
                  className="carousel-header-container"
                  style={{
                    transition: platformTransitionStyle(
                      'transform 500ms ease-in-out'
                    ),
                  }}
                >
                  <>
                    <h2
                      className={`carousel-header ${
                        collection.content[0].item_type === 'video'
                          ? 'video'
                          : showcase === 'showcase'
                          ? 'showcase'
                          : ''
                      }`}
                    >
                      {collection.title}
                    </h2>
                  </>
                </div>
                <div
                  className="slider-wrapper"
                  style={{
                    transition: platformTransitionStyle(
                      'transform 500ms ease-in-out'
                    ),
                  }}
                >
                  <div
                    className="slider"
                    style={{
                      transition: platformTransitionStyle(
                        'transform 500ms ease-in-out'
                      ),
                      transform: `translateX(${slideOffset}px)`,
                    }}
                  >
                    {collection && collection.content && (
                      <>
                        {collection.content.map((r, i) => (
                          <div
                            key={`${r.cid}`}
                            ref={
                              this.state.currentIndex === i
                                ? this.selectedCarouselElementRef
                                : null
                            }
                          >
                            <CarouselItem
                              slug={slug}
                              ref={
                                this.state.currentIndex === i
                                  ? this.selectedCarouselItemRef
                                  : null
                              }
                              parent={this}
                              parentTitle={parentTitle}
                              carouselTitle={this.carouselTitle}
                              carouselSlug={collection.slug}
                              emptySet={emptySet}
                              key={i}
                              content={r}
                              hideTitle={hideTitle}
                              hideMetaData={hideMetaData}
                              view={showcase ? 'showcase' : r.item_type}
                              index={i}
                              itemCount={itemCount}
                            />
                          </div>
                        ))}
                        {this.hasMoreContent && (
                          <SeeMoreCarouselItem
                            content={{
                              title: `See All ${
                                collection.content[0].item_type === 'show'
                                  ? 'Shows'
                                  : collection.content[0].item_type === 'video'
                                  ? 'Videos'
                                  : collection.content[0].item_type ===
                                    'skylab-collection'
                                  ? 'Collections'
                                  : collection.content[0].item_type === 'season'
                                  ? 'Seasons'
                                  : ''
                              }`,
                            }}
                            listHasSeasons={listHasSeasons}
                            parent={this}
                            parentTitle={parentTitle}
                            view={collection.content[0].item_type}
                            slug={slug}
                            url={collection.url}
                            header={collection.title}
                            ref={this.hasMoreContentButtonRef}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </>
    )
  }
}

Carousel.propTypes = {
  collection: PropTypes.shape({
    content: PropTypes.arrayOf(
      PropTypes.shape({
        length: PropTypes.number,
        title: PropTypes.string,
        item_type: PropTypes.string,
        cid: PropTypes.string,
      })
    ),
    meta: PropTypes.shape({
      links: PropTypes.shape({
        next: PropTypes.string,
      }),
    }),
    slug: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  hideMetaData: PropTypes.bool,
  hideTitle: PropTypes.bool,
  listHasSeasons: PropTypes.object,
  parentTitle: PropTypes.string,
  resetCarousel: PropTypes.bool,
  slug: PropTypes.string,
  ui: PropTypes.shape({
    focusableComponents: PropTypes.shape({
      screen: PropTypes.string,
    }),
  }),
  emptySet: PropTypes.bool,
  handleNearEndOfCarousel: PropTypes.func,
}

const mapStateToProps = (state) => {
  return {
    ui: state.ui,
  }
}

export default compose(
  connect(mapStateToProps, null, null, { forwardRef: true })(
    focusable(Carousel)
  )
)
